/**
 * OrderJourneyTimeline
 * A chronological “story” of the job: Lead → Quote → Order → Booking → Invoices
 */

import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, Button } from "@mui/material";
import { Timeline, Tag, Empty } from "antd";
import { format } from "date-fns";
import { Launch } from "@mui/icons-material";
import { openAppPopup } from "@/shared/utils/openAppPopup.js";

const safeDate = (value) => {
  if (!value) return null;
  try {
    const d = new Date(value);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
};

const formatDateTime = (value) => {
  const d = safeDate(value);
  if (!d) return "N/A";
  try {
    return format(d, "PPp");
  } catch {
    return d.toISOString();
  }
};

export default function OrderJourneyTimeline({ order, quote, lead, invoices }) {
  const items = useMemo(() => {
    const events = [];

    if (lead) {
      events.push({
        key: `lead:${lead.id || lead._id}`,
        date: safeDate(lead.createdAt) || safeDate(lead.updatedAt),
        title: `Lead ${lead.referenceNumber || lead.id || lead._id || ""}`.trim(),
        tag: { color: "blue", label: String(lead.status || lead.tracking?.lifecycleStage || "lead").toUpperCase() },
        description: lead.moving_from && lead.moving_to ? `${lead.moving_from} → ${lead.moving_to}` : null,
        route: lead.id || lead._id ? `/leads/${lead.id || lead._id}` : null,
        routeTitle: `Lead ${lead.id || lead._id || ""}`.trim(),
      });
    }

    if (quote) {
      events.push({
        key: `quote:${quote.id || quote._id}`,
        date: safeDate(quote.createdAt) || safeDate(quote.updatedAt),
        title: `Quote ${quote.id || quote._id || ""}`.trim(),
        tag: { color: "purple", label: String(quote.status || "quote").replaceAll("_", " ").toUpperCase() },
        description:
          typeof quote.estimatedCost === "number"
            ? `Estimated: R${quote.estimatedCost.toLocaleString("en-ZA")}`
            : null,
        route: quote.id || quote._id ? `/quotes/${quote.id || quote._id}` : null,
        routeTitle: `Quote ${quote.id || quote._id || ""}`.trim(),
      });
    }

    if (order) {
      events.push({
        key: `order:${order.id}`,
        date: safeDate(order.createdAt) || safeDate(order.updatedAt),
        title: `Order ${order.id}`,
        tag: { color: "green", label: String(order.status || "order").replaceAll("_", " ").toUpperCase() },
        description: order.notes || null,
        route: order.id ? `/orders/${order.id}` : null,
        routeTitle: `Order ${order.id || ""}`.trim(),
      });

      if (order.booking?.moveDate || order.booking?.status) {
        events.push({
          key: `booking:${order.id}`,
          date: safeDate(order.booking?.moveDate) || safeDate(order.updatedAt),
          title: "Booking",
          tag: { color: "cyan", label: String(order.booking?.status || "scheduled").replaceAll("_", " ").toUpperCase() },
          description: order.booking?.moveDate ? `Move date: ${formatDateTime(order.booking.moveDate)}` : null,
          route: order.id ? `/bookings/${order.id}` : null,
          routeTitle: `Booking ${order.id || ""}`.trim(),
        });
      }
    }

    const invoiceList = Array.isArray(invoices) ? invoices : [];
    for (const inv of invoiceList) {
      const invId = inv.id || inv._id;
      events.push({
        key: `invoice:${invId}`,
        date: safeDate(inv.createdAt) || safeDate(inv.updatedAt),
        title: `Invoice ${invId || ""}`.trim(),
        tag: { color: "gold", label: String(inv.status || "invoice").replaceAll("_", " ").toUpperCase() },
        description:
          typeof inv.total === "number"
            ? `Total: R${inv.total.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : null,
        route: invId ? `/invoices/${invId}` : null,
        routeTitle: `Invoice ${invId || ""}`.trim(),
      });
    }

    return events
      .filter((e) => e.date || e.title)
      .sort((a, b) => {
        const aTime = a.date ? a.date.getTime() : 0;
        const bTime = b.date ? b.date.getTime() : 0;
        return bTime - aTime;
      })
      .map((e) => ({
        key: e.key,
        children: (
          <Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {e.route ? (
                <Button
                  size="small"
                  variant="text"
                  endIcon={<Launch fontSize="small" />}
                  sx={{ px: 0, minWidth: 0, textTransform: "none", fontWeight: 600 }}
                  onClick={() => openAppPopup(e.route, { title: e.routeTitle || "Record" })}
                >
                  {e.title}
                </Button>
              ) : (
                <Typography variant="subtitle2">{e.title}</Typography>
              )}
              <Tag color={e.tag.color}>{e.tag.label}</Tag>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {e.date ? formatDateTime(e.date) : "N/A"}
            </Typography>
            {e.description ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {e.description}
              </Typography>
            ) : null}
          </Box>
        ),
      }));
  }, [order, quote, lead, invoices]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Journey
        </Typography>
        {items.length === 0 ? (
          <Empty description="No journey events yet" />
        ) : (
          <Timeline items={items} />
        )}
      </CardContent>
    </Card>
  );
}

